const GAMIFICATION_NAV_ITEMS = [
  { id: 'gamification-dashboard', label: 'Dashboard' },
  { id: 'gamification-levels', label: 'Levels' },
  { id: 'gamification-challenges', label: 'Challenges' },
  { id: 'gamification-competitions', label: 'Competitions' },
  { id: 'gamification-rewards', label: 'Rewards' },
  { id: 'gamification-rankings', label: 'Rankings' },
  { id: 'gamification-gift-vouchers', label: 'Gift Vouchers' },
  { id: 'gamification-achievements', label: 'Achievements' },
  { id: 'gamification-leaderboards', label: 'Leaderboards' },
];

const GAMIFICATION_MANAGER_ITEMS = [
  ...GAMIFICATION_NAV_ITEMS,
  { id: 'gamification-campaigns', label: 'Campaigns' },
];

const GAMIFICATION_ADMIN_ITEMS = [
  ...GAMIFICATION_MANAGER_ITEMS,
  { id: 'gamification-settings', label: 'Settings' },
];


// Self-service groups shared by every in-tenant role. An employee, a manager
// and an HR admin all have their own learning, their own goals and their own
// signed-in devices — the data is scoped server-side, so the nav is identical.
const GROWTH_NAV_ITEM = {
  id: 'growth',
  label: 'Growth',
  iconKey: 'reports',
  defaultSubItemId: 'growth-learning',
  subItems: [
    { id: 'growth-learning', label: 'Learning' },
    { id: 'growth-skills', label: 'My Skills' },
    { id: 'growth-okrs', label: 'Goals & OKRs' },
    { id: 'growth-review', label: 'My Review' },
  ],
};

const WORKPLACE_NAV_ITEM = {
  id: 'workplace',
  label: 'Workplace',
  iconKey: 'employees',
  defaultSubItemId: 'workplace-announcements',
  subItems: [
    { id: 'workplace-announcements', label: 'Announcements' },
    { id: 'workplace-kb', label: 'Knowledge Base' },
    { id: 'workplace-directory', label: 'People' },
    { id: 'workplace-org-chart', label: 'Org Chart' },
    { id: 'workplace-referrals', label: 'Referrals' },
  ],
};

const ACCOUNT_NAV_ITEM = {
  id: 'account',
  label: 'My Account',
  iconKey: 'profile',
  defaultSubItemId: 'account-notifications',
  subItems: [
    { id: 'account-notifications', label: 'Notifications' },
    { id: 'account-devices', label: 'Signed-in Devices' },
    { id: 'account-tax-statement', label: 'Tax Statement' },
    { id: 'account-loans', label: 'Loans & Advances' },
  ],
};

// Where someone goes when they are stuck, identical for every role — the two
// halves of support sit together rather than one being buried under Workplace.
//
// `workplace-help` keeps its id because WorkplaceModule renders it and every
// dashboard already dispatches on the `workplace-` prefix; renaming it would
// mean a branch in each of the three dashboards for no user-visible gain.
//
// The default is Help & FAQs, not Helpdesk: helpdesk is gated on the helpdesk
// module, so on a tenant without it the group would otherwise open onto a tab
// that had been filtered out of the menu.
const SUPPORT_NAV_ITEM = {
  id: 'support',
  label: 'Support',
  iconKey: 'helpdesk',
  defaultSubItemId: 'workplace-help',
  subItems: [
    { id: 'workplace-help', label: 'Help & FAQs' },
    // Same `workplace-` prefix as the tab above, so all three dashboards route
    // it to WorkplaceModule already and none of them needed a new branch.
    { id: 'workplace-support', label: 'Contact Support' },
    { id: 'helpdesk', label: 'Helpdesk' },
  ],
};

export const SELF_SERVICE_NAV_ITEMS = [
  GROWTH_NAV_ITEM,
  WORKPLACE_NAV_ITEM,
  ACCOUNT_NAV_ITEM,
];

export const EMPLOYEE_NAV_SCHEMA = [
  { id: 'workspace', label: 'My Workspace', iconKey: 'workspace' },
  { id: 'dashboard', label: 'Dashboard', iconKey: 'dashboard' },
  { id: 'ai-assistant-chat', label: 'AI Assistant', iconKey: 'ai' },
  { id: 'attendance', label: 'Attendance', iconKey: 'attendance' },
  { id: 'assets', label: 'My Assets', iconKey: 'assets' },
  {
    id: 'leaves',
    label: 'Leaves',
    iconKey: 'leaves',
    subItems: [
      { id: 'leave-history', label: 'History' },
      { id: 'leave-apply', label: 'Apply Leave' },
      { id: 'leave-holidays', label: 'Holidays' },
      { id: 'leave-requests', label: 'Requests' },
      { id: 'leave-regularization', label: 'Regularization' },
      { id: 'leave-wfh', label: 'Work From Home' },
      { id: 'leave-outstation', label: 'Outstation Duty' },
    ],
  },
  { id: 'team', label: 'My Team', iconKey: 'team' },
  SUPPORT_NAV_ITEM,
  {
    id: 'engagement-rewards',
    label: 'Engagement & Rewards',
    iconKey: 'gamification',
    defaultSubItemId: 'gamification-dashboard',
    subItems: GAMIFICATION_NAV_ITEMS,
  },
  { id: 'pulse-surveys', label: 'Survey Inbox', iconKey: 'reports' },
  { id: 'performance-report', label: 'Performance Report', iconKey: 'reports' },
  {
    id: 'finance',
    label: 'Finance',
    iconKey: 'finance',
    subItems: [
      { id: 'finance-home', label: 'Overview' },
      { id: 'finance-payslips', label: 'Payslips' },
      { id: 'finance-reimbursements', label: 'Expenses' },
    ],
  },
  {
    id: 'benefits',
    label: 'Benefits',
    iconKey: 'benefits',
    subItems: [
      { id: 'benefits-my', label: 'My Benefits' },
      { id: 'benefits-plans', label: 'Benefit Plans' },
    ],
  },
  ...SELF_SERVICE_NAV_ITEMS,
  { id: 'profile', label: 'My Profile', iconKey: 'profile' },
];

export const MANAGER_NAV_SCHEMA = [
  { id: 'dashboard', label: 'Dashboard', iconKey: 'dashboard' },
  { id: 'ai-assistant-chat', label: 'AI Assistant', iconKey: 'ai' },
  { id: 'assets', label: 'Assets', iconKey: 'assets' },
  {
    id: 'leaves',
    label: 'Leaves',
    iconKey: 'leaves',
    subItems: [
      { id: 'leave-history', label: 'History' },
      { id: 'leave-apply', label: 'Apply Leave' },
      { id: 'leave-holidays', label: 'Holidays' },
      { id: 'leave-regularization', label: 'Regularization' },
      { id: 'leave-wfh', label: 'Work From Home' },
      { id: 'leave-outstation', label: 'Outstation Duty' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    iconKey: 'finance',
    subItems: [
      { id: 'finance-home', label: 'Overview' },
      { id: 'finance-payslips', label: 'Payslips' },
      { id: 'finance-reimbursements', label: 'Expenses' },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    iconKey: 'reports',
    subItems: [
      { id: 'report-center', label: 'Report Center' },
      { id: 'report-attendance', label: 'Attendance Report' },
      { id: 'report-muster', label: 'Muster Register' },
      { id: 'report-leave', label: 'Leave Report' },
      { id: 'report-payroll', label: 'Payroll Report' },
      { id: 'report-balance', label: 'Leave Balance' },
      { id: 'report-statutory', label: 'Statutory Compliance' },
      { id: 'report-performance', label: 'Performance Report' },
      { id: 'report-executive', label: 'Executive Analytics' },
    ],
  },
  { id: 'team', label: 'My Team', iconKey: 'team' },
  SUPPORT_NAV_ITEM,
  {
    id: 'engagement-rewards',
    label: 'Engagement & Rewards',
    iconKey: 'gamification',
    defaultSubItemId: 'gamification-dashboard',
    subItems: GAMIFICATION_MANAGER_ITEMS,
  },
  {
    id: 'team-ops',
    label: 'Team Operations',
    iconKey: 'teamOps',
    subItems: [
      { id: 'tasks', label: 'Tasks' },
      { id: 'schedule', label: 'Schedule' },
      { id: 'workload', label: 'Workload' },
      { id: 'conflicts', label: 'Conflicts' },
    ],
  },
  { id: 'profile', label: 'My Profile', iconKey: 'profile' },
  ...SELF_SERVICE_NAV_ITEMS,
];

// An hr_manager runs HR operations for their reporting line, so they get the
// manager console plus the HR-ops surfaces — the same components the admin
// console mounts, scoped server-side to the people they manage. A plain
// `manager` does not see these.
const HR_MANAGER_NAV_EXTRAS = [
  { id: 'action-center', label: 'HR Action Center', iconKey: 'actionCenter' },
  { id: 'hr-operations', label: 'HR Operations', iconKey: 'onboarding' },
  { id: 'hr-lifecycle', label: 'HR Lifecycle', iconKey: 'employees' },
  { id: 'exit-management', label: 'Exit Management', iconKey: 'employees' },
  { id: 'contracts', label: 'Contracts', iconKey: 'onboarding' },
  { id: 'doc-templates', label: 'Document Templates', iconKey: 'reports' },
  { id: 'pulse-surveys', label: 'Pulse Surveys', iconKey: 'reports' },
];

// Leave administration an hr_manager has but a plain manager does not.
const HR_MANAGER_LEAVE_EXTRAS = [
  { id: 'leave-approvals-inbox', label: 'Approvals Inbox' },
  { id: 'leave-requests', label: 'Requests' },
  { id: 'leave-types', label: 'Leave Types' },
  { id: 'leave-policies', label: 'Leave Policies' },
];

export const getManagerNavSchema = ({ isHrManager = false } = {}) => {
  if (!isHrManager) return MANAGER_NAV_SCHEMA;

  const withLeaveExtras = MANAGER_NAV_SCHEMA.map((item) =>
    item.id === 'leaves'
      ? { ...item, subItems: [...item.subItems, ...HR_MANAGER_LEAVE_EXTRAS] }
      : item
  );

  // HR ops sits above "My Team" so the operational work leads, and the
  // self-service items stay where a manager already expects them.
  const teamIndex = withLeaveExtras.findIndex((item) => item.id === 'team');
  const insertAt = teamIndex === -1 ? withLeaveExtras.length : teamIndex;
  return [
    ...withLeaveExtras.slice(0, insertAt),
    ...HR_MANAGER_NAV_EXTRAS,
    ...withLeaveExtras.slice(insertAt),
  ];
};

// The admin console carried fifty top-level entries, several of them the same
// screen reached twice, and a "Master" group of twenty-eight unrelated items —
// departments, ESI challans, tax slabs and geofences in one list. Everything is
// filed here under the job it belongs to, two levels deep because that is all
// the sidebar renders: attendance rules sit with attendance, statutory filings
// sit together, the Education suite is one group instead of fourteen entries.
//
// Every leaf id is unchanged. Ids are what the dashboards dispatch on and what
// deep links carry, so regrouping moves labels around and breaks no route.
const ADMIN_NAV_SCHEMA_BASE = [
  { id: 'dashboard', label: 'Dashboard', iconKey: 'dashboard' },
  {
    id: 'workflows',
    label: 'Workflows',
    iconKey: 'reports',
    defaultSubItemId: 'workflows-instances',
    subItems: [
      { id: 'workflows-instances', label: 'Instances' },
      { id: 'workflows-inbox', label: 'Notifications' },
      { id: 'workflows-builder', label: 'Builder' },
      { id: 'workflows-delegations', label: 'Delegations' },
    ],
  },
  { id: 'ai-command', label: 'AI Dashboard', iconKey: 'ai' },
  {
    id: 'ai-center',
    label: 'AI Center',
    iconKey: 'ai',
    defaultSubItemId: 'ai-center-attendance-insight',
    subItems: [
      { id: 'ai-center-attendance-insight', label: 'AI Attendance Insight' },
      { id: 'ai-center-hiring-forecast', label: 'AI Hiring Forecast' },
      { id: 'ai-center-payroll-forecast', label: 'AI Payroll Forecast' },
      { id: 'ai-center-team-performance', label: 'AI Team Performance' },
      { id: 'ai-center-settings', label: 'AI Settings' },
    ],
  },
  {
    id: 'ai-assistant',
    label: 'AI Assistant',
    iconKey: 'ai',
    defaultSubItemId: 'ai-assistant-chat',
    subItems: [
      { id: 'ai-assistant-chat', label: 'Chat' },
      { id: 'ai-assistant-agents', label: 'Agents' },
      { id: 'ai-assistant-usage', label: 'Usage' },
      { id: 'ai-assistant-settings', label: 'Settings' },
    ],
  },

  // ── People ──────────────────────────────────────────────────────────────
  // Who works here and how their record is set up. Payroll, statutory and
  // attendance configuration used to live in this group too; they now sit with
  // the module they configure.
  {
    id: 'master',
    label: 'Master Data',
    iconKey: 'master',
    subItems: [
      { id: 'employees', label: 'Employees' },
      { id: 'master-departments', label: 'Departments' },
      { id: 'master-subdepartments', label: 'Sub-Departments' },
      { id: 'master-designations', label: 'Designations' },
      { id: 'onboarding-checklists', label: 'Onboarding Checklists' },
    ],
  },
  {
    id: 'hr-ops',
    label: 'HR Operations',
    iconKey: 'onboarding',
    subItems: [
      { id: 'action-center', label: 'Action Center' },
      { id: 'hr-operations', label: 'Daily Operations' },
      { id: 'hr-lifecycle', label: 'Lifecycle' },
      { id: 'onboarding-employees', label: 'Onboarding' },
      { id: 'onboarding-documents', label: 'Onboarding Documents' },
      { id: 'contracts', label: 'Contracts' },
      { id: 'doc-templates', label: 'Document Templates' },
      { id: 'exit-management', label: 'Exit Management' },
    ],
  },

  // ── Workforce ───────────────────────────────────────────────────────────
  // `attendance` stays the id of the summary screen and becomes this group's
  // first tab; the group needed an id of its own so the two never collide.
  {
    id: 'attendance-hub',
    label: 'Attendance',
    iconKey: 'attendance',
    subItems: [
      { id: 'attendance', label: 'Summary' },
      { id: 'rules-shifts', label: 'Shift Configuration' },
      { id: 'rules-penalties', label: 'Attendance Rules' },
      { id: 'geofence', label: 'Geofence' },
      { id: 'fraud', label: 'Fraud Signals' },
    ],
  },
  {
    id: 'leaves',
    label: 'Leaves',
    iconKey: 'leaves',
    subItems: [
      { id: 'leave-my-history', label: 'My History' },
      { id: 'leave-my-apply', label: 'Apply Leave' },
      { id: 'leave-approvals-inbox', label: 'Approvals Inbox' },
      { id: 'leave-requests', label: 'Requests' },
      { id: 'leave-regularization', label: 'Regularization' },
      { id: 'leave-wfh', label: 'Work From Home' },
      { id: 'leave-outstation', label: 'Outstation Duty' },
      { id: 'leave-types', label: 'Leave Types' },
      { id: 'leave-policies', label: 'Leave Policies' },
      { id: 'leave-holidays', label: 'Holidays' },
      { id: 'leave-my-holidays', label: 'My Holidays' },
    ],
  },
  { id: 'assets', label: 'Assets', iconKey: 'assets' },
  { id: 'team', label: 'My Team', iconKey: 'team' },

  // ── Finance ─────────────────────────────────────────────────────────────
  {
    id: 'finance',
    label: 'Finance',
    iconKey: 'finance',
    subItems: [
      { id: 'finance-home', label: 'Overview' },
      { id: 'finance-my', label: 'My Salary' },
      { id: 'finance-process', label: 'Process Payroll' },
      { id: 'finance-payslips', label: 'Payslips' },
      { id: 'finance-reimbursements', label: 'Expenses' },
      { id: 'finance-config', label: 'Salary Config' },
      { id: 'finance-structures', label: 'Salary Structures' },
      { id: 'finance-tax-slabs', label: 'Tax Slabs' },
      { id: 'final-settlements', label: 'Final Settlements' },
    ],
  },
  // ESI, PF and the two TDS forms are filings, not payroll setup: same deadline
  // calendar, same statutory portal, nothing else in Finance behaves like them.
  {
    id: 'statutory',
    label: 'Statutory',
    iconKey: 'finance',
    subItems: [
      { id: 'esi-policy', label: 'ESI Policy' },
      { id: 'esi-branches', label: 'ESI Branches' },
      { id: 'esi-employees', label: 'ESI Employees' },
      { id: 'esi-contributions', label: 'ESI Contributions' },
      { id: 'esi-challans', label: 'ESI Challans' },
      { id: 'pf-policy', label: 'PF Policy' },
      { id: 'pf-employees', label: 'PF Members (UAN)' },
      { id: 'pf-contributions', label: 'PF Contributions' },
      { id: 'pf-challans', label: 'PF Challans' },
      { id: 'tax-form16', label: 'Form 16' },
      { id: 'tax-form24q', label: 'Form 24Q' },
    ],
  },
  {
    id: 'benefits',
    label: 'Benefits',
    iconKey: 'benefits',
    subItems: [
      { id: 'benefits-plans', label: 'Benefit Plans' },
      { id: 'benefits-enrollment', label: 'Enrollment' },
    ],
  },
  {
    id: 'equity-hub',
    label: 'Equity',
    iconKey: 'equity',
    subItems: [
      { id: 'equity', label: 'Grants & Vesting' },
      { id: 'my-equity', label: 'My Equity' },
    ],
  },

  // ── Engagement ──────────────────────────────────────────────────────────
  {
    id: 'engagement-rewards',
    label: 'Engagement & Rewards',
    iconKey: 'gamification',
    defaultSubItemId: 'gamification-dashboard',
    subItems: GAMIFICATION_ADMIN_ITEMS,
  },
  { id: 'pulse-surveys', label: 'Pulse Surveys', iconKey: 'reports' },

  // ── Education Suite ─────────────────────────────────────────────────────
  // One group rather than fourteen top-level entries. Each tab is licensed on
  // its own edu_* module key (planAccess.TAB_MODULE_MAP), and the group itself
  // disappears when a tenant holds none of them — filterNavByModuleAccess drops
  // a group left with no sub-items.
  {
    id: 'education',
    label: 'Education',
    iconKey: 'academic',
    subItems: [
      { id: 'edu-schools', label: 'Schools' },
      { id: 'edu-departments', label: 'Departments' },
      { id: 'edu-faculty', label: 'Faculty' },
      { id: 'edu-calendar', label: 'Academic Calendar' },
      { id: 'edu-timetable', label: 'Timetable' },
      { id: 'edu-courses', label: 'Courses' },
      { id: 'edu-rooms', label: 'Rooms' },
      { id: 'edu-contracts', label: 'Faculty Contracts' },
      { id: 'edu-accreditation', label: 'Accreditation' },
      { id: 'edu-research', label: 'Research & Publications' },
      { id: 'edu-grievance', label: 'Grievance' },
      { id: 'edu-student-workers', label: 'Student Workers' },
      { id: 'edu-adjunct', label: 'Adjunct Faculty' },
      { id: 'edu-integrations', label: 'Integrations' },
    ],
  },

  // ── Insights ────────────────────────────────────────────────────────────
  {
    id: 'reports',
    label: 'Reports',
    iconKey: 'reports',
    subItems: [
      { id: 'report-center', label: 'Report Center' },
      { id: 'report-employee', label: 'Employee Report' },
      { id: 'report-attendance', label: 'Attendance Report' },
      { id: 'report-muster', label: 'Muster Register' },
      { id: 'report-leave', label: 'Leave Report' },
      { id: 'report-payroll', label: 'Payroll Report' },
      { id: 'report-balance', label: 'Leave Balance' },
      { id: 'report-statutory', label: 'Statutory Compliance' },
      { id: 'report-performance', label: 'Performance Report' },
      { id: 'report-executive', label: 'Executive Analytics' },
    ],
  },

  // ── Account / administration ────────────────────────────────────────────
  { id: 'workspace', label: 'Workspace', iconKey: 'workspace' },
  // Rebranding sits beside Workspace because that is what it configures: what
  // this organisation looks like and how its mail arrives. Grouped rather than
  // scattered so the palette, the sending addresses and the templates that use
  // them are all one place — they are useless apart.
  {
    id: 'rebranding',
    label: 'Rebranding',
    iconKey: 'workspace',
    defaultSubItemId: 'rebranding-look',
    subItems: [
      { id: 'rebranding-look', label: 'Look & Feel' },
      { id: 'rebranding-senders', label: 'Sending Addresses' },
      { id: 'rebranding-templates', label: 'Email Templates' },
      { id: 'rebranding-channels', label: 'Channels' },
    ],
  },
  // Who may do what inside this workspace. Scoped server-side to the tenant and
  // to roles below the caller's own — see routes_access_control._require_access_admin.
  { id: 'access-control', label: 'Roles & Access', iconKey: 'workspace' },
  {
    id: 'org',
    label: 'Organisation',
    iconKey: 'legalEntity',
    subItems: [
      { id: 'org-legal-entities', label: 'Legal Entities' },
      { id: 'org-business-units', label: 'Business Units' },
    ],
  },
  {
    id: 'platform',
    label: 'Integrations & Security',
    iconKey: 'integrations',
    subItems: [
      { id: 'integrations', label: 'Integrations' },
      { id: 'enterprise-sso', label: 'SSO & Provisioning' },
      { id: 'session-governance', label: 'Devices & Sessions' },
    ],
  },
  { id: 'plan-catalog', label: 'Plan & Billing', iconKey: 'finance' },
  { id: 'profile', label: 'My Profile', iconKey: 'profile' },
  ...SELF_SERVICE_NAV_ITEMS,
  SUPPORT_NAV_ITEM,
];

export const getAdminNavSchema = ({ includeReturnToSuperadmin = false } = {}) => {
  if (!includeReturnToSuperadmin) return ADMIN_NAV_SCHEMA_BASE;
  return [
    ...ADMIN_NAV_SCHEMA_BASE,
    { id: 'return_superadmin', label: 'Return to Superadmin', iconKey: 'workspace' },
  ];
};

// Removes the AI Assistant navigation (group + chat/agents/usage/settings) when
// the tenant is not entitled to the AI Assistant add-on. The chat route itself
// is independently gated server-side (license check), so this only controls
// visibility. Pass entitled=true to keep the items.
export const filterAiAssistantNav = (schema = [], entitled = false) => {
  if (entitled) return schema;
  return schema.filter((item) => !String(item.id || '').startsWith('ai-assistant'));
};

export const mapNavItemsWithIcons = (schema, iconMap) => {
  return schema.map((item) => ({
    ...item,
    icon: iconMap[item.iconKey],
    subItems: item.subItems ? [...item.subItems] : undefined,
  }));
};

// ---- Sidebar section grouping (additive, non-breaking) ----
// Ordered section labels used to render grouped navigation.
export const NAV_SECTIONS = [
  'Overview',
  'AI',
  'People',
  'Workforce',
  'Finance',
  'Engagement',
  'Education',
  'Insights',
  'Account',
  'Support',
];

// Always rendered last, whatever else is in the sidebar — see groupNavItems.
export const TRAILING_SECTION = 'Support';

// Maps a nav item id -> section label. Covers employee, manager and admin
// schemas. Ids not listed here fall into an unlabeled trailing group, so the
// grouping degrades gracefully for any future items.
export const SECTION_BY_ID = {
  // Self-service groups, filed where their subject already lives.
  growth: 'Engagement',
  workplace: 'People',
  account: 'Account',
  // Overview
  dashboard: 'Overview',
  workspace: 'Overview',
  workflows: 'Overview',
  rebranding: 'Account',
  // Groups introduced when the fifty flat admin entries were filed by subject.
  'hr-ops': 'People',
  'attendance-hub': 'Workforce',
  statutory: 'Finance',
  'equity-hub': 'Finance',
  education: 'Education',
  org: 'Account',
  platform: 'Account',
  'access-control': 'Account',
  // AI
  'ai-command': 'AI',
  'ai-center': 'AI',
  'ai-assistant': 'AI',
  'ai-assistant-chat': 'AI',
  'ai-assistant-agents': 'AI',
  'ai-assistant-usage': 'AI',
  'ai-assistant-settings': 'AI',
  // People
  master: 'People',
  'action-center': 'People',
  'hr-lifecycle': 'People',
  'exit-management': 'People',
  contracts: 'People',
  'doc-templates': 'People',
  onboarding: 'People',
  // Workforce
  attendance: 'Workforce',
  leaves: 'Workforce',
  assets: 'Workforce',
  team: 'Workforce',
  // Support (helpdesk is a sub-item of it now, so it needs no mapping itself)
  support: 'Support',
  'team-ops': 'Workforce',
  // Finance
  finance: 'Finance',
  benefits: 'Finance',
  // Engagement
  'engagement-rewards': 'Engagement',
  'pulse-surveys': 'Engagement',
  'performance-report': 'Engagement',
  // Education Suite
  'edu-schools': 'Education',
  'edu-departments': 'Education',
  'edu-faculty': 'Education',
  'edu-calendar': 'Education',
  'edu-timetable': 'Education',
  'edu-courses': 'Education',
  'edu-rooms': 'Education',
  'edu-contracts': 'Education',
  'edu-accreditation': 'Education',
  'edu-research': 'Education',
  'edu-grievance': 'Education',
  'edu-student-workers': 'Education',
  'edu-adjunct': 'Education',
  'edu-integrations': 'Education',
  // Insights
  reports: 'Insights',
  fraud: 'Insights',
  // Account
  equity: 'Finance',
  'my-equity': 'Finance',
  'org-legal-entities': 'Account',
  'org-business-units': 'Account',
  integrations: 'Account',
  'enterprise-sso': 'Account',
  'session-governance': 'Account',
  'plan-catalog': 'Account',
  profile: 'Account',
  return_superadmin: 'Account',
};

// Groups a flat, icon-mapped nav array into ordered sections.
// Returns [{ section: string|null, items: [...] }]. A null section means the
// group should render without a header (unmapped / fallback items).
export const groupNavItems = (items) => {
  if (!Array.isArray(items) || items.length === 0) return [];
  const buckets = new Map();
  items.forEach((item) => {
    const section = SECTION_BY_ID[item.id] || '__more__';
    if (!buckets.has(section)) buckets.set(section, []);
    buckets.get(section).push(item);
  });
  const ordered = [
    ...NAV_SECTIONS.filter((s) => s !== TRAILING_SECTION && buckets.has(s)),
    ...[...buckets.keys()].filter(
      (s) => s === '__more__' || !NAV_SECTIONS.includes(s)
    ),
    // Support sits below even the unlabeled fallback group. Listing it last in
    // NAV_SECTIONS would not be enough — anything unmapped renders after every
    // named section, so it has to be appended after that bucket too.
    ...(buckets.has(TRAILING_SECTION) ? [TRAILING_SECTION] : []),
  ];
  return ordered.map((section) => ({
    section: section === '__more__' ? null : section,
    items: buckets.get(section),
  }));
};
